import { assets } from "../../Assets/assets";

// Replace with your actual hosted logo URL or base64 data URI
const LOGO_URL = assets.qqtlogo;

const numberToWords = (num) => {
  if (isNaN(num) || num === null || num === undefined) return "Zero Only";
  const ones = [
    "",
    "One",
    "Two",
    "Three",
    "Four",
    "Five",
    "Six",
    "Seven",
    "Eight",
    "Nine",
    "Ten",
    "Eleven",
    "Twelve",
    "Thirteen",
    "Fourteen",
    "Fifteen",
    "Sixteen",
    "Seventeen",
    "Eighteen",
    "Nineteen",
  ];
  const tens = [
    "",
    "",
    "Twenty",
    "Thirty",
    "Forty",
    "Fifty",
    "Sixty",
    "Seventy",
    "Eighty",
    "Ninety",
  ];
  const convert = (n) => {
    n = Math.floor(n);
    if (n < 0) return "";
    if (n === 0) return "";
    if (n < 20) return ones[n];
    if (n < 100)
      return (
        tens[Math.floor(n / 10)] + (n % 10 !== 0 ? " " + ones[n % 10] : "")
      );
    if (n < 1000)
      return (
        ones[Math.floor(n / 100)] +
        " Hundred" +
        (n % 100 !== 0 ? " and " + convert(n % 100) : "")
      );
    if (n < 100000)
      return (
        convert(Math.floor(n / 1000)) +
        " Thousand" +
        (n % 1000 !== 0 ? " " + convert(n % 1000) : "")
      );
    if (n < 10000000)
      return (
        convert(Math.floor(n / 100000)) +
        " Lakh" +
        (n % 100000 !== 0 ? " " + convert(n % 100000) : "")
      );
    return (
      convert(Math.floor(n / 10000000)) +
      " Crore" +
      (n % 10000000 !== 0 ? " " + convert(n % 10000000) : "")
    );
  };
  const whole = Math.floor(num);
  if (whole === 0) return "Zero Only";
  return convert(whole).trim() + " Only";
};

// ─────────────────────────────────────────────────────────────
// Invoice Amount Helpers
// ─────────────────────────────────────────────────────────────

const roundInvoiceAmount = (amount) => {
  const n = Number(amount || 0);
  return n % 1 !== 0 ? Math.round(n * 100) / 100 : Math.round(n);
};

const formatInvoiceAmount = (amount) => {
  const n = Number(amount || 0);
  if (isNaN(n)) return "0";
  return n % 1 !== 0
    ? n.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : n.toLocaleString("en-IN");
};

const formatInvoiceRate = (amount) => {
  const n = Number(amount || 0);
  if (isNaN(n)) return "0";
  return n % 1 !== 0
    ? n.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : n.toLocaleString("en-IN");
};

export const getInvoiceHTMLWeb = (orderData, role = "buyer") => {
  const safeVal = (val) =>
    val && val.toString().trim() !== "" && val !== "N/A" && val !== "NaN"
      ? val
      : '<span style="color:#D84545; font-style:italic;">Not Yet</span>';

  const isProforma = orderData?.isProforma || false;
  const quotation = orderData?.quotation || {};
  const isSampleReq = orderData?.isSampleReq === true;

  const dateStr = orderData?.createdAt
    ? new Date(orderData.createdAt).toLocaleDateString("en-GB")
    : new Date().toLocaleDateString("en-GB");

  const invoiceNo = safeVal(orderData?.orderNumber);
  const quotationNo = safeVal(
    quotation?.quotationId || quotation?._id?.slice(-6).toUpperCase(),
  );

  const variety = safeVal(
    orderData?.productVariety?.varietyName ||
      orderData?.productVarietyName ||
      orderData?.customVarietyName ||
      orderData?.product?.name,
  );
  const batchNo = safeVal(
    quotation?.batch_Lot_Number ||
      quotation?.batchNumber ||
      quotation?.lotNumber ||
      orderData?.batch_Lot_Number ||
      orderData?.product?.batchNumber ||
      orderData?.productVariety?.batchNumber,
  );

  const breakdownKey = isSampleReq ? "sample" : "fullOrder";

  const priceBreakdown =
    orderData?.priceBreakdown ||
    quotation?.priceBreakdown ||
    orderData?.freshOrderData?.priceBreakdown;

  const breakdown =
    orderData?.breakdown ||
    priceBreakdown?.[breakdownKey]?.breakdown ||
    priceBreakdown?.[breakdownKey] ||
    null;

  const sampleWeight = Number(
    breakdown?.packSizeWeight || quotation?.packSize?.weight || 1,
  );

  const qty =
    Number(breakdown?.totalWeightKg) ||
    Number(orderData?.priceAndQuantity?.MOQ) ||
    Number(quotation?.quantity?.volume) ||
    Number(orderData?.quantity) ||
    0;
  const unit =
    orderData?.priceAndQuantity?.standardizedUnit ||
    quotation?.standardizedUnit ||
    "Kgs";

  const price = Number(
    breakdown?.pricePerKg ||
      quotation?.pricing?.commissionAdjustedAmount ||
      quotation?.pricing?.amount ||
      0,
  );

  const commissionDetails =
    breakdown?.commissionDetails || quotation?.commissionDetails || {};
  const isCommissionSet = commissionDetails?.isCommissionSet || false;
  const commissionValue = Number(
    commissionDetails?.value ?? commissionDetails?.commission ?? 0,
  );
  const commissionType =
    commissionDetails?.typeOfCommission ||
    commissionDetails?.commissionType ||
    "Percentage"; // "Percentage" or "Fixed"

  // Sample vs Full Order display qty
  const displayQty = isSampleReq
    ? sampleWeight
    : (breakdown?.totalWeightKg || qty);
  const packagesCount = Math.ceil(displayQty / sampleWeight);
  const unitGrossWeight = sampleWeight + 0.2;
  const totalGrossWeight = packagesCount * unitGrossWeight;

  const totalAmount = Number(
    breakdown?.baseAmount != null
      ? breakdown.baseAmount
      : displayQty * price,
  );

  // Calculate commission amount
  let commissionAmount = Number(commissionDetails?.commissionInAmount ?? 0);
  if (!commissionAmount && isCommissionSet && commissionValue > 0) {
    if (commissionType === "Percentage") {
      commissionAmount = (totalAmount * commissionValue) / 100;
    } else {
      commissionAmount = commissionValue;
    }
  }

  const gstRate = Number(
    breakdown?.gstRate != null
      ? breakdown.gstRate
      : quotation?.pricing?.gstRate ||
          orderData?.productVariety?.gstRate ||
          orderData?.product?.gstRate ||
          12,
  );

  const gstAmount = Number(
    breakdown?.gstAmount != null
      ? breakdown.gstAmount
      : (totalAmount * gstRate) / 100,
  );

  // ── Dynamic Transport Charges ───────────────────────────
  const transportInfo = quotation?.transportDetails?.find(
    (t) => t?.charge_is_for === (isSampleReq ? "Sample" : "Full_Order"),
  );

  const transportCharge = Number(
    breakdown?.transportationCharges != null
      ? breakdown.transportationCharges
      : transportInfo?.transportationAmount ||
          transportInfo?.transportation_charge_price ||
          transportInfo?.transportationCharges ||
          0,
  );

  const modeOfTransport = transportInfo?.modeOfTransportation
    ? `By ${transportInfo.modeOfTransportation}`
    : "By Road / Surface";

  const courierCharge = transportCharge;
  const grandTotal = Number(
    breakdown?.totalAmount != null
      ? breakdown.totalAmount
      : totalAmount + gstAmount + courierCharge + commissionAmount,
  );

  const roundedGrandTotal = roundInvoiceAmount(grandTotal);

  const sellerInfo = {
    name: "PCM EXPORTS",
    address:
      "Shop No. 104, Block-A, Bharati Tower, Forestpark Ekamra Marg, Bhubaneswar, Khordha, Odisha(India) - 751009",
    tel: "+91-6752455372",
    email: "pcmexportsbbsr@gmail.com",
    web: "www.pcmexports.com",
    gstin: "21BBFPM2533K1ZW",
    pan: "BILPS0248G",
  };

  const createdBy = orderData?.createdBy || {};
  const buyerName = safeVal(
    [createdBy?.firstName, createdBy?.lastName].filter(Boolean).join(" ") ||
      orderData?.buyer?.name ||
      orderData?.buyerName ||
      orderData?.user?.name,
  );

  const addr = orderData?.address || orderData?.buyer?.address || {};
  const deliveryAddress = safeVal(
    typeof addr === "string"
      ? addr
      : [
          addr?.flat,
          addr?.floor,
          addr?.area,
          addr?.landmark,
          addr?.addressLine1,
          addr?.addressLine2,
          addr?.city,
          addr?.state,
          addr?.country,
          addr?.pincode,
        ]
          .filter(Boolean)
          .join(", <br/> "),
  );

  const buyerGstin = safeVal(
    orderData?.createdBy?.GST ||
      orderData?.buyer?.gstin ||
      createdBy?.gstNumber ||
      createdBy?.gstin ||
      orderData?.gstin,
  );

  return `
    <style>
        @page { margin: 20px; size: A4 portrait; }
        body { font-family: Arial, sans-serif; color: #000; margin: 0; padding: 0; font-size: 10px; line-height: 1.4; }
        .container { width: 100%; margin: 0 auto; padding: 20px; box-sizing: border-box; }
        .top-right-ribbon { position: absolute; right: 0; top: 10px; text-align: right; font-size: 9px; }
        .top-right-ribbon div { margin-bottom: 3px; }
        .accent-bar { height: 4px; background-color: #EB5C0F; width: 100px; margin-left: auto; margin-top: 4px; }
        .header-grid { display: flex; width: 100%; margin-bottom: 15px; position: relative; justify-content: space-between; align-items: flex-start; }
        .logo-box img { height: 75px; object-fit: contain; }
        .meta-info { margin-bottom: 15px; font-size: 11px; font-weight: bold; display: flex; justify-content: space-between; }
        .party-container { display: flex; width: 100%; justify-content: space-between; margin-bottom: 20px; }
        .party-box { width: 48%; }
        .party-title { font-weight: bold; font-size: 12px; margin-bottom: 8px; }
        .party-name { color: #EB5C0F; font-size: 14px; font-weight: bold; margin-bottom: 6px; }
        .party-details { font-size: 10px; line-height: 1.5; color: #000; }
        .section-title { font-weight: bold; font-size: 11px; margin-bottom: 6px; }
        table { width: 100%; border-collapse: collapse; margin-bottom: 0; }
        th, td { border: 1px solid #000; text-align: center; padding: 8px 4px; font-size: 9px; vertical-align: middle; }
        th { background-color: #0C3E61; color: #FFF; font-weight: bold; }
        .text-left { text-align: left; }
        .text-right { text-align: right; }
        .col-sno { width: 4%; }
        .col-desc { width: 28%; }
        .col-batch { width: 12%; }
        .col-uw { width: 11%; }
        .col-qty { width: 10%; }
        .col-unit { width: 7%; }
        .col-rate { width: 13%; }
        .col-total { width: 15%; }
        .summary-block { display: flex; width: 100%; border: 1px solid #000; border-top: none; }
        .sum-col { flex: 1; border-right: 1px solid #000; box-sizing: border-box; }
        .sum-col:last-child { border-right: none; }
        .dark-header { background-color: #0C3E61; color: #fff; font-weight: bold; text-align: center; padding: 8px 4px; border-bottom: 1px solid #000; font-size: 9px; }
        .white-cell { text-align: center; padding: 8px 4px; border-bottom: 1px solid #000; font-size: 9px; }
        .white-cell:last-child { border-bottom: none; }
        .words-bar { font-weight: bold; font-size: 12px; padding: 10px; border: 1px solid #000; border-top: none; color: #EB5C0F; background-color: #fdf6f2; }
        .bottom-grid { display: flex; width: 100%; margin-top: 15px; justify-content: space-between; }
        .b-table { width: 48%; border-collapse: collapse; }
        .b-table th, .b-table td { border: 1px solid #000; padding: 6px; font-size: 9px; }
        .b-table th { background-color: #0C3E61; color: #fff; text-align: left; width: 45%; }
        .b-table td { background-color: #fff; color: #000; font-weight: bold; }
        .footer-section { display: flex; width: 100%; border: 1px solid #000; margin-top: 25px; }
        .terms-box { width: 50%; border-right: 1px solid #000; padding: 10px; box-sizing: border-box; }
        .sign-box { width: 50%; padding: 10px; box-sizing: border-box; text-align: right; position: relative; }
        .terms-heading { font-weight: bold; text-decoration: underline; margin-bottom: 6px; font-size: 11px; }
        .terms-list { margin: 0; padding-left: 0; list-style-type: none; font-size: 9px; line-height: 1.4; }
        .digital-stamp { font-size: 9px; color: #666; margin-top: 30px; margin-bottom: 10px; }
        .auth-label { font-weight: bold; font-size: 11px; text-decoration: underline; position: absolute; bottom: 10px; right: 10px; }
    </style>
    <div class="container">
        <div class="header-grid">
            <div class="logo-box">
                <img src="${LOGO_URL}" alt="Company Logo" />
            </div>
            <div class="top-right-ribbon" style="position: static; margin-left: auto;">
                <div>GSTIN - ${sellerInfo.gstin}</div>
                <div class="accent-bar"></div>
                <div class="accent-bar" style="width: 80px; height: 2px;"></div>
            </div>
        </div>

        <div class="meta-info">
<div>Order No.: ${invoiceNo} &nbsp;&nbsp;|&nbsp;&nbsp; Document: ${isProforma ? "PROFORMA INVOICE" : "TAX INVOICE"} &nbsp;&nbsp;|&nbsp;&nbsp; Type: <span style="color:#EB5C0F; font-weight:bold;">${isSampleReq ? "SAMPLE ORDER" : "FULL ORDER"}</span></div>
            <div>Date: ${new Date().toLocaleDateString("en-IN")}</div>
        </div>

        <div class="party-container">
            <div class="party-box">
                <div class="party-title">Exporter Details</div>
                <div class="party-name">${sellerInfo.name}</div>
                <div class="party-details">
                    ${sellerInfo.address}<br>
                    Mobile: ${sellerInfo.tel}<br>
                    Mail id: ${sellerInfo.email}<br>
                    Website: ${sellerInfo.web}
                </div>
            </div>
            <div class="party-box" style="padding-left: 20px;">
                <div class="party-title">Buyer Details</div>
                <div class="party-details" style="color: #000;">
                    <span style="font-weight: bold; font-size: 11px;">${buyerName}</span><br>
                    ${deliveryAddress}<br>
                    <div style="margin-top: 4px;"><strong>GSTIN:</strong> ${buyerGstin}</div>
                </div>
            </div>
        </div>

        <div class="section-title">Product Details (${isSampleReq ? "Sample" : "Full Order"})</div>

        <table>
            <thead>
                <tr>
                    <th class="col-sno">S.<br>No.</th>
                    <th class="col-desc text-left">Description</th>
                    <th class="col-batch">Batch/Lot<br>Number</th>
                    <th class="col-uw">Unit Net<br>Weight (Kgs)</th>
                    <th class="col-qty">Qty</th>
                    <th class="col-unit">Unit</th>
                    <th class="col-rate">Unit Rate<br>(INR)</th>
                    <th class="col-total">Total (INR)</th>
                </tr>
            </thead>
            <tbody>
                <tr>
                    <td>1</td>
                    <td class="text-left" style="font-weight: bold;">${variety}</td>
                    <td>${batchNo}</td>
                    <td>${sampleWeight > 0 ? sampleWeight.toFixed(1) : safeVal(0)}</td>
                    <td>${displayQty}</td>
                    <td>${unit}</td>
                    <td>${formatInvoiceRate(price)}</td>
                    <td class="text-right" style="font-weight:bold;">${formatInvoiceAmount(totalAmount)}</td>
                </tr>
               <tr>
    <td></td>
    <td colspan="6" class="text-right">
        <strong>GST (${gstRate}%)</strong>
    </td>

    <td class="text-right">
        ${formatInvoiceAmount(gstAmount)}
    </td>
</tr>
                ${
                  courierCharge > 0
                    ? `<tr>
                    <td></td>
                    <td colspan="6" class="text-right"><strong>${isSampleReq ? "Courier Charge" : "Freight & Handling"}</strong></td>
                    <td class="text-right">${formatInvoiceAmount(courierCharge)}</td>
                </tr>`
                    : ""
                }
                    <tr style="background:#f9f9f9;">
  <td></td>
  <td colspan="6" class="text-right">
    <strong>
      Total Amount (Including GST & Other Charges)
    </strong>
  </td>
  <td class="text-right">
    <strong>
      ₹${formatInvoiceAmount(grandTotal)}
    </strong>
  </td>
</tr>
            </tbody>
        </table>

        <div class="summary-block">
  <div class="sum-col" style="border-right:none;">
    <div
      class="dark-header"
      style="font-size:12px;padding:12px;"
    >
      Invoice Total Payable (Including GST & Other Charges)
    </div>

    <div
      class="white-cell"
      style="
        height:70px;
        display:flex;
        align-items:center;
        justify-content:center;
        font-size:22px;
        font-weight:bold;
      "
    >
      ₹${formatInvoiceAmount(grandTotal)}
    </div>
  </div>
</div>

        <div class="words-bar">
            Invoice Total in Words - ₹ ${numberToWords(roundedGrandTotal)}
        </div>

        <div class="bottom-grid">
    <table class="b-table">
        <tr><th>Mode of Transport</th><td>${modeOfTransport}</td></tr>
        <tr><th>Country of Origin of Goods</th><td>India</td></tr>
    </table>
    <table class="b-table">
        <tr><th>Port of Loading</th><td></td></tr>
        <tr><th>Place of Delivery</th><td>As per Buyer Address</td></tr>
    </table>
</div>

        <div class="footer-section">
            <div class="terms-box">
                <div class="terms-heading">Terms &amp; Conditions</div>
                <ul class="terms-list">
                    <li>E.&amp; O.E.</li>
                    <li>1. Goods once sold will not be taken back.</li>
                    <li>2. Interest @ 18% p.a. will be charged if payment is not made within the stipulated time.</li>
                    <li>3. Subject to 'Bhubaneswar' Jurisdiction only.</li>
                </ul>
            </div>
            <div class="sign-box">
                <div style="font-weight: bold; font-size: 12px;">For ${sellerInfo.name}</div>
                <div class="digital-stamp">
                    Digitally generated by QQT Portal<br>
                    Date: ${new Date().toLocaleDateString("en-IN")} ${new Date().toLocaleTimeString("en-IN")}
                </div>
                <div class="auth-label">Authorised Signatory</div>
            </div>
        </div>
    </div>
  `;
};

export const getBulkInvoiceHTMLWeb = ({
  order = {},
  quotations = [],
  isSampleReq = true,
  isProforma = true,
}) => {
  // ── Reuse same helpers from the file (numberToWords, safeVal, LOGO_URL) ───
  // These are already defined above this function in invoiceTemplateWeb.js.
  // We reference them directly.

  const safeVal = (val) =>
    val && val.toString().trim() !== "" && val !== "N/A" && val !== "NaN"
      ? val
      : '<span style="color:#D84545; font-style:italic;">Not Yet</span>';

  const dateStr = order?.createdAt
    ? new Date(order.createdAt).toLocaleDateString("en-GB")
    : new Date().toLocaleDateString("en-GB");

  const invoiceNo = safeVal(order?.orderNumber);

  const variety = safeVal(
    order?.productVariety?.varietyName ||
      order?.product?.name ||
      order?.customVarietyName,
  );

  const sellerInfo = {
    name: "PCM EXPORTS",
    address:
      "Shop No. 104, Block-A, Bharati Tower, Forestpark Ekamra Marg, Bhubaneswar, Khordha, Odisha(India) - 751009",
    tel: "+91-6752455372",
    email: "pcmexportsbbsr@gmail.com",
    web: "www.pcmexports.com",
    gstin: "21BBFPM2533K1ZW",
    pan: "BILPS0248G",
  };

  const createdBy = order?.createdBy || {};
  const buyerName = safeVal(
    [createdBy?.firstName, createdBy?.lastName].filter(Boolean).join(" ") ||
      order?.buyer?.name,
  );

  const addr = order?.address || order?.buyer?.address || {};
  const deliveryAddress = safeVal(
    typeof addr === "string"
      ? addr
      : [
          addr?.flat,
          addr?.floor,
          addr?.area,
          addr?.landmark,
          addr?.addressLine1,
          addr?.addressLine2,
          addr?.city,
          addr?.state,
          addr?.country,
          addr?.pincode,
        ]
          .filter(Boolean)
          .join(", <br/>"),
  );

  const buyerGstin = safeVal(order?.createdBy?.GST);

  // ── Per-quotation row data ─────────────────────────────────────────────────
  let combinedTotal = 0;
  let combinedGst = 0;
  let combinedCommission = 0;
  let combinedTransport = 0;
  const transportModesUsed = new Set();

  const tableRows = quotations.map((q, idx) => {
    const breakdown = q?.cartPriceBreakDown || {};

    const sampleWeight = Number(q?.packSize?.weight || 1);
    const qty = sampleWeight;
    const price = Number(q?.pricing?.commissionAdjustedAmount || 0);

    const rowBase = Number(breakdown.baseAmount ?? qty * price);
    const rowGst = Number(breakdown.gstAmount ?? 0);

    // Transport from quotation transportDetails
    const tInfo = q?.transportDetails?.find(
      (t) => t?.charge_is_for === "Sample",
    );
    const rowTransport = Number(
      breakdown.transportationCharges ?? tInfo?.transportationAmount ?? 0,
    );
    if (tInfo?.modeOfTransportation) {
      transportModesUsed.add(tInfo.modeOfTransportation);
    }

    const rowTotal = Number(
      breakdown.totalAmount ?? rowBase + rowGst + rowTransport,
    );
    const batchNo = safeVal(q?.batch_Lot_Number);
    const unitGross = sampleWeight + 0.2;

    combinedTotal += rowTotal;
    combinedGst += rowGst;
    combinedTransport += rowTransport;

    return `
    <tr>
      <td>${idx + 1}</td>
      <td class="text-left" style="font-weight: bold;">
        ${variety}
        <br/><span style="font-size:8px; color:#666;">#${
          q.quotationUniqueNumber || q._id?.slice(-6) || ""
        }</span>
      </td>
      <td>${batchNo}</td>
      <td>${sampleWeight > 0 ? sampleWeight.toFixed(1) : safeVal(0)}</td>
      <td>${qty}</td>
      <td>${safeVal(q?.packSize?.unit || q?.standardizedUnit || "Kgs")}</td>
      <td>${formatInvoiceRate(price)}</td>
      <td class="text-right" style="font-weight:bold;">
        ${formatInvoiceAmount(rowTotal)}
      </td>
    </tr>
  `;
  });

  // GST row (combined)
  const gstRow = `
  <tr>
    <td></td>
    <td colspan="6" class="text-right"><strong>GST — All Quotations</strong></td>
    <td class="text-right">${formatInvoiceAmount(combinedGst)}</td>
  </tr>
`;

  // Transport row (combined, if any)
  const transportRow =
    combinedTransport > 0
      ? `
    <tr>
      <td></td>
      <td colspan="6" class="text-right"><strong>Courier Charges — All Quotations</strong></td>
      <td class="text-right">${formatInvoiceAmount(combinedTransport)}</td>
    </tr>
  `
      : "";

  const grandTotal = combinedTotal;

  const roundedGrandTotal = roundInvoiceAmount(grandTotal);

  const modeOfTransport =
    transportModesUsed.size === 0
      ? "By Road / Surface"
      : transportModesUsed.size === 1
        ? `By ${[...transportModesUsed][0]}`
        : `By ${[...transportModesUsed].join(" / ")}`;

  // Summary block values (use first quotation for pack/unit reference)
  const firstQ = quotations[0] || {};
  const firstSampleWeight = Number(firstQ?.packSize?.weight || 1);
  const totalDisplayQty = quotations.reduce(
    (s, q) => s + Number(q?.packSize?.weight || 1),
    0,
  );
  const totalPackages = quotations.length;
  const unitGross = firstSampleWeight + 0.2;
  const totalGrossWeight = quotations.reduce(
    (s, q) => s + (Number(q?.packSize?.weight || 1) + 0.2),
    0,
  );

  return `
    <style>
        @page { margin: 20px; size: A4 portrait; }
        body { font-family: Arial, sans-serif; color: #000; margin: 0; padding: 0; font-size: 10px; line-height: 1.4; }
        .container { width: 100%; margin: 0 auto; padding: 20px; box-sizing: border-box; }
        .top-right-ribbon { position: static; margin-left: auto; text-align: right; font-size: 9px; }
        .top-right-ribbon div { margin-bottom: 3px; }
        .accent-bar { height: 4px; background-color: #EB5C0F; width: 100px; margin-left: auto; margin-top: 4px; }
        .header-grid { display: flex; width: 100%; margin-bottom: 15px; position: relative; justify-content: space-between; align-items: flex-start; }
        .logo-box img { height: 75px; object-fit: contain; }
        .meta-info { margin-bottom: 15px; font-size: 11px; font-weight: bold; display: flex; justify-content: space-between; }
        .party-container { display: flex; width: 100%; justify-content: space-between; margin-bottom: 20px; }
        .party-box { width: 48%; }
        .party-title { font-weight: bold; font-size: 12px; margin-bottom: 8px; }
        .party-name { color: #EB5C0F; font-size: 14px; font-weight: bold; margin-bottom: 6px; }
        .party-details { font-size: 10px; line-height: 1.5; color: #000; }
        .section-title { font-weight: bold; font-size: 11px; margin-bottom: 6px; }
        table { width: 100%; border-collapse: collapse; margin-bottom: 0; }
        th, td { border: 1px solid #000; text-align: center; padding: 8px 4px; font-size: 9px; vertical-align: middle; }
        th { background-color: #0C3E61; color: #FFF; font-weight: bold; }
        .text-left { text-align: left; }
        .text-right { text-align: right; }
        .col-sno { width: 4%; }
        .col-desc { width: 28%; }
        .col-batch { width: 12%; }
        .col-uw { width: 11%; }
        .col-qty { width: 10%; }
        .col-unit { width: 7%; }
        .col-rate { width: 13%; }
        .col-total { width: 15%; }
        .summary-block { display: flex; width: 100%; border: 1px solid #000; border-top: none; }
        .sum-col { flex: 1; border-right: 1px solid #000; box-sizing: border-box; }
        .sum-col:last-child { border-right: none; }
        .dark-header { background-color: #0C3E61; color: #fff; font-weight: bold; text-align: center; padding: 8px 4px; border-bottom: 1px solid #000; font-size: 9px; }
        .white-cell { text-align: center; padding: 8px 4px; border-bottom: 1px solid #000; font-size: 9px; }
        .white-cell:last-child { border-bottom: none; }
        .words-bar { font-weight: bold; font-size: 12px; padding: 10px; border: 1px solid #000; border-top: none; color: #EB5C0F; background-color: #fdf6f2; }
        .bottom-grid { display: flex; width: 100%; margin-top: 15px; justify-content: space-between; }
        .b-table { width: 48%; border-collapse: collapse; }
        .b-table th, .b-table td { border: 1px solid #000; padding: 6px; font-size: 9px; }
        .b-table th { background-color: #0C3E61; color: #fff; text-align: left; width: 45%; }
        .b-table td { background-color: #fff; color: #000; font-weight: bold; }
        .footer-section { display: flex; width: 100%; border: 1px solid #000; margin-top: 25px; }
        .terms-box { width: 50%; border-right: 1px solid #000; padding: 10px; box-sizing: border-box; }
        .sign-box { width: 50%; padding: 10px; box-sizing: border-box; text-align: right; position: relative; }
        .terms-heading { font-weight: bold; text-decoration: underline; margin-bottom: 6px; font-size: 11px; }
        .terms-list { margin: 0; padding-left: 0; list-style-type: none; font-size: 9px; line-height: 1.4; }
        .digital-stamp { font-size: 9px; color: #666; margin-top: 30px; margin-bottom: 10px; }
        .auth-label { font-weight: bold; font-size: 11px; text-decoration: underline; position: absolute; bottom: 10px; right: 10px; }
    </style>
    <div class="container">
        <div class="header-grid">
            <div class="logo-box">
                <img src="${LOGO_URL}" alt="Company Logo" />
            </div>
            <div class="top-right-ribbon">
                <div>PAN No - ${sellerInfo.pan}</div>
                <div>GSTIN - ${sellerInfo.gstin}</div>
                <div class="accent-bar"></div>
                <div class="accent-bar" style="width: 80px; height: 2px;"></div>
            </div>
        </div>

        <div class="meta-info">
          <div>Order No.: ${invoiceNo} &nbsp;&nbsp;|&nbsp;&nbsp; Document: ${
            isProforma ? "PROFORMA INVOICE" : "TAX INVOICE"
          } &nbsp;&nbsp;|&nbsp;&nbsp; Type: <span style="color:#EB5C0F; font-weight:bold;">SAMPLE ORDER (${
            quotations.length
          } Quotations)</span></div>
          <div>Date: ${new Date().toLocaleDateString("en-IN")}</div>
        </div>

        <div class="party-container">
            <div class="party-box">
                <div class="party-title">Exporter Details</div>
                <div class="party-name">${sellerInfo.name}</div>
                <div class="party-details">
                    ${sellerInfo.address}<br>
                    Mobile: ${sellerInfo.tel}<br>
                    Mail id: ${sellerInfo.email}<br>
                    Website: ${sellerInfo.web}
                </div>
            </div>
            <div class="party-box" style="padding-left: 20px;">
                <div class="party-title">Buyer Details</div>
                <div class="party-details" style="color: #000;">
                    <span style="font-weight: bold; font-size: 11px;">${buyerName}</span><br>
                    ${deliveryAddress}<br>
                    <div style="margin-top: 4px;"><strong>GSTIN:</strong> ${buyerGstin}</div>
                </div>
            </div>
        </div>

        <div class="section-title">Product Details (Sample — ${quotations.length} Quotations)</div>

        <table>
            <thead>
                <tr>
                    <th class="col-sno">S.<br>No.</th>
                    <th class="col-desc text-left">Description</th>
                    <th class="col-batch">Batch/Lot<br>Number</th>
                    <th class="col-uw">Unit Net<br>Weight (Kgs)</th>
                    <th class="col-qty">Qty</th>
                    <th class="col-unit">Unit</th>
                    <th class="col-rate">Unit Rate<br>(INR)</th>
                    <th class="col-total">Total (INR)</th>
                </tr>
            </thead>
<tbody>
  ${tableRows.join("")}
  ${gstRow}
  ${transportRow}
</tbody>
        </table>

        <div class="summary-block">
  <div class="sum-col" style="border-right:none;">
    <div
      class="dark-header"
      style="font-size:12px;padding:12px;"
    >
      Invoice Total Payable (Including GST & Other Charges)
    </div>

    <div
      class="white-cell"
      style="
        height:70px;
        display:flex;
        align-items:center;
        justify-content:center;
        font-size:22px;
        font-weight:bold;
      "
    >
      ₹${formatInvoiceAmount(grandTotal)}
    </div>
  </div>
</div>

        <div class="words-bar">
            Invoice Total in Words - ₹ ${numberToWords(roundedGrandTotal)}
        </div>

       <div class="bottom-grid">
    <table class="b-table">
        <tr><th>Mode of Transport</th><td>${modeOfTransport}</td></tr>
        <tr><th>Country of Origin of Goods</th><td>India</td></tr>
    </table>
    <table class="b-table">
        <tr><th>Port of Loading</th><td></td></tr>
        <tr><th>Place of Delivery</th><td>As per Buyer Address</td></tr>
    </table>
</div>

        <div class="footer-section">
            <div class="terms-box">
                <div class="terms-heading">Terms &amp; Conditions</div>
                <ul class="terms-list">
                    <li>E.&amp; O.E.</li>
                    <li>1. Goods once sold will not be taken back.</li>
                    <li>2. Interest @ 18% p.a. will be charged if payment is not made within the stipulated time.</li>
                    <li>3. Subject to 'Bhubaneswar' Jurisdiction only.</li>
                </ul>
            </div>
            <div class="sign-box">
                <div style="font-weight: bold; font-size: 12px;">For ${sellerInfo.name}</div>
                <div class="digital-stamp">
                    Digitally generated by QQT Portal<br>
                    Date: ${new Date().toLocaleDateString("en-IN")} ${new Date().toLocaleTimeString("en-IN")}
                </div>
                <div class="auth-label">Authorised Signatory</div>
            </div>
        </div>
    </div>
  `;
};
